import React, { useState, useRef, useEffect } from 'react';
import useAnimationFrame from './useAnimationFrame';
import { Store } from './Store';
import { formatNumber, spriteUrl } from './format';
import Toast from './Toast';
import CodeRain, { backgroundProgress } from './CodeRain';
import useCrowd from './useCrowd';
import { workers, promotions, workerUpgradeTiers, REPUTATION_BONUS } from '../game/data';
import {
    defaultGameValues, migrateSave, getCps, getClickPower, getPromotion, getWorkerMultiplier, getSynergyBonus,
    addLines, click, buyWorker, buyUpgrade, getPendingReputation, shipRelease, getCrowdBonus,
} from '../game/rules';

// How often worker income is added to the counter, and how often the game autosaves.
const TICK_INTERVAL_MS = 100;
const SAVE_INTERVAL_MS = 10000;
// How long a floating "+N" from a click stays on screen.
const HIT_MS = 900;

// Returns the starting state, plus the lines workers earned while the game was closed.
function initState() {
    const previousSession = loadSession();
    if (previousSession == null) {
        return { values: defaultGameValues(), offlineGain: 0 };
    }

    const { values, lastSaved } = previousSession;
    const secondsAway = lastSaved ? Math.max(0, (Date.now() - lastSaved) / 1000) : 0;
    const offlineGain = getCps(values) * secondsAway;
    return { values: addLines(values, offlineGain), offlineGain };
}

function saveSession(state){
    try {
        localStorage.setItem('session', JSON.stringify({ ...state, lastSaved: Date.now() }));
    } catch (e) {
        console.warn('Could not save session.', e);
    }
}

function loadSession(){
    let saved;
    try {
        saved = JSON.parse(localStorage.getItem('session'));
    } catch (e) {
        console.warn('Ignoring unreadable save.', e);
        return null;
    }
    if (!saved || typeof saved !== 'object') return null;
    return { values: migrateSave(saved), lastSaved: saved.lastSaved };
}

function IconTile({ icon, size = 'w-8 h-8' }) {
    return (
        <span className={`${size} shrink-0 p-1 rounded-md bg-platinum`}>
            <img src={spriteUrl(icon)} alt="" className="w-full h-full object-contain" />
        </span>
    );
}

const Clicker = () => {
    const [initial] = useState(initState);
    const [currentValues, setCurrentValues] = useState(initial.values);
    const [offlineGain, setOfflineGain] = useState(initial.offlineGain);
    const tickTimeRef = useRef(0);
    const saveTimeRef = useRef(0);
    const [effect, setEffect] = useState(false);
    const mainRef = useRef(null);
    const codeRainRef = useRef(null);

    // Players online right now (null = not connected, so solo). The count rides
    // along on the state the rules see, but loaded saves always start solo.
    const crowd = useCrowd();
    const crowdRef = useRef(1);
    crowdRef.current = crowd || 1;
    const withCrowd = (state) => (state.crowd === crowdRef.current ? state : { ...state, crowd: crowdRef.current });
    const live = withCrowd(currentValues);
    const [hits, setHits] = useState([]);
    const hitIdRef = useRef(0);

    // Latest state for saves that happen outside of render (timers, page events).
    const stateRef = useRef(currentValues);
    useEffect(() => {
        stateRef.current = currentValues;
    }, [currentValues]);

    // Save when the tab is hidden or closed, so offline progress starts from there.
    useEffect(() => {
        const persist = () => saveSession(stateRef.current);
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') persist();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('beforeunload', persist);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('beforeunload', persist);
        };
    }, []);

    // All updates go through functional setState with the pure rules, so clicks,
    // ticks and purchases landing in the same frame can't overwrite each other.
    // Diablo-style floating number at the cursor for each click. Each one is
    // removed on a timer, so they never pile up even if animations don't run.
    const showHit = (event, amount) => {
        const rect = mainRef.current.getBoundingClientRect();
        const id = ++hitIdRef.current;
        const hit = {
            id,
            text: `+${formatNumber(amount)}`,
            x: event.clientX - rect.left + (Math.random() - 0.5) * 40,
            y: event.clientY - rect.top,
            drift: Math.round((Math.random() - 0.5) * 60),
        };
        setHits(previous => [...previous.slice(-29), hit]);
        setTimeout(() => setHits(previous => previous.filter(h => h.id !== id)), HIT_MS);
    }

    const incrementClick = (event) => {
        // Ignore keyboard "clicks" (Enter held down) so the button can't be auto-fired.
        if (event.clientX > 0 && event.clientY > 0) {
            setCurrentValues(previousValues => click(withCrowd(previousValues)));
            setEffect(true);
            const amount = getClickPower(live);
            showHit(event, amount);
            codeRainRef.current?.burst(amount);
        }
    }

    // Income is paid for the actual time elapsed, so a slow frame or a tab
    // coming back from the background catches up instead of losing time.
    useAnimationFrame((deltaTime) => {
        tickTimeRef.current = tickTimeRef.current + (deltaTime*1000);
        saveTimeRef.current = saveTimeRef.current + (deltaTime*1000);

        if (tickTimeRef.current >= TICK_INTERVAL_MS) {
            const elapsedSeconds = tickTimeRef.current / 1000;
            tickTimeRef.current = 0;
            setCurrentValues(previousValues => {
                const state = withCrowd(previousValues);
                return addLines(state, getCps(state) * elapsedSeconds);
            });
        }

        if (saveTimeRef.current >= SAVE_INTERVAL_MS) {
            saveTimeRef.current = 0;
            saveSession(stateRef.current);
        }
    });

    const onBuyWorker = (type) => setCurrentValues(previousValues => buyWorker(withCrowd(previousValues), type));
    const onBuyUpgrade = (id) => setCurrentValues(previousValues => buyUpgrade(withCrowd(previousValues), id));

    const onShipRelease = () => {
        const pending = getPendingReputation(currentValues);
        if (!window.confirm(`Ship a release? Your lines, workers and upgrades reset, and you gain ${pending} reputation (+${Math.round(pending * REPUTATION_BONUS * 100)}% to all output, forever).`)) return;
        setCurrentValues(previousValues => {
            const next = shipRelease(previousValues);
            saveSession(next);
            return next;
        });
    }

    const save = () => {
        saveSession(currentValues);
    }

    const reset = () => {
        if (!window.confirm('Reset everything, including reputation from releases?')) return;
        localStorage.removeItem('session');
        setCurrentValues(defaultGameValues());
        setOfflineGain(0);
    }

    const promotion = getPromotion(live);
    const nextPromotion = promotions.find(p => p.level === live.upgrades.click + 1);
    const clickPower = getClickPower(live);

    return (
        <div className="w-full min-h-screen flex flex-col lg:flex-row">
            <main ref={mainRef} className="relative flex-1 overflow-hidden">
                <CodeRain
                    ref={codeRainRef}
                    cps={getCps(live)}
                    progress={backgroundProgress(getCps(live), live.upgrades.click)} />
                <div className="relative min-h-full flex flex-col items-center justify-center gap-6 p-6 text-white">
                    <div className="text-center">
                        <p className="text-sm uppercase tracking-wider text-white/80">Uncommitted lines of code</p>
                        <p className="text-5xl sm:text-6xl font-bold tabular-nums">{formatNumber(Math.floor(live.counters.lines))}</p>
                        <p className="text-lg">{formatNumber(getCps(live))} per second</p>
                        {crowd !== null && (
                            <p className="mt-1 inline-flex items-center gap-2 text-xs text-white/80">
                                <span className="w-2 h-2 rounded-full bg-avocado motion-safe:animate-pulse" />
                                {crowd <= 1
                                    ? "You're the only dev online. Others boost you when they join."
                                    : `${crowd} devs coding right now · +${Math.round(getCrowdBonus(live) * 100)}% crowd bonus`}
                            </p>
                        )}
                    </div>

                    <button
                        onClick={incrementClick}
                        onAnimationEnd={() => setEffect(false)}
                        aria-label="Write code"
                        className={`${effect && "animate-wiggle"} w-56 h-56 sm:w-64 sm:h-64 rounded-full bg-rose-500 border-4 border-white/40 shadow-xl
                            flex flex-col items-center justify-center gap-2 select-none hover:bg-rose-400 active:scale-95 transition-transform`}>
                        <img src={spriteUrl('keyboard')} alt="" draggable={false} className="w-36 sm:w-44" />
                        <span className="text-sm font-semibold">+{formatNumber(clickPower)} line{clickPower === 1 ? '' : 's'}</span>
                    </button>

                    <div className="-mt-2 flex flex-col items-center gap-1 text-center">
                        {promotion && (
                            <p key={promotion.level} className="flex items-center gap-2 motion-safe:animate-pop-in">
                                <IconTile icon={promotion.icon} />
                                <span className="font-semibold">{promotion.name}</span>
                                <span className="text-sm text-white/80">{promotion.short_description}</span>
                            </p>
                        )}
                        {nextPromotion && (
                            <p className="text-xs text-white/70">
                                Next promotion: {nextPromotion.name}
                                {live.counters.click < nextPromotion.requirement
                                    ? ` at ${formatNumber(nextPromotion.requirement)} lines clicked`
                                    : ' available in the store'}
                            </p>
                        )}
                    </div>

                    <ReleasePanel state={live} onShip={onShipRelease} />
                    <ProgressPanel state={live} />

                    <div className="flex flex-col items-center gap-3">
                        <p className="text-xs text-white/70">
                            Total lines clicked: {formatNumber(live.counters.click)}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={save}
                                className="rounded-md bg-avocado py-2 px-4 text-sm font-semibold text-white shadow-sm">
                                    Save
                            </button>
                            <button
                                onClick={reset}
                                className="rounded-md border border-white/50 py-2 px-4 text-sm font-semibold text-white">
                                    Reset
                            </button>
                        </div>
                        <p className="text-xs text-white/60">
                            Sprites by <a className="underline" href="https://kenney.nl" target="_blank" rel="noreferrer">Kenney</a> (CC0)
                        </p>
                    </div>
                </div>
                {hits.map(hit => (
                    <span
                        key={hit.id}
                        aria-hidden="true"
                        style={{ left: hit.x, top: hit.y, '--drift': `${hit.drift}px` }}
                        className="absolute z-20 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none
                            text-3xl font-black text-avocado [text-shadow:0_2px_0_#162334,0_0_6px_#162334] motion-safe:animate-float-up">
                        {hit.text}
                    </span>
                ))}
            </main>

            <Store
                state={live}
                onBuyWorker={onBuyWorker}
                onBuyUpgrade={onBuyUpgrade} />

            {offlineGain >= 1 && (
                <Toast onDone={() => setOfflineGain(0)}>
                    Written while you were away: <span className="font-bold">{formatNumber(offlineGain)}</span> lines
                </Toast>
            )}
        </div>
    );
}

// Reputation from past releases, and the button to ship the current run.
function ReleasePanel({ state, onShip }) {
    const pending = getPendingReputation(state);
    const { points, releases } = state.reputation;
    if (pending <= 0 && points <= 0) return null;

    return (
        <section className="w-full max-w-sm rounded-lg bg-oxford-blue/30 p-4 text-sm motion-safe:animate-pop-in">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <p className="font-semibold">Reputation: {points}</p>
                    <p className="text-xs text-white/70">
                        +{Math.round(points * REPUTATION_BONUS * 100)}% to all output · {releases} release{releases === 1 ? '' : 's'} shipped
                    </p>
                </div>
                {pending > 0 && (
                    <button
                        onClick={onShip}
                        className="shrink-0 rounded-md bg-avocado py-2 px-3 text-sm font-semibold shadow-sm hover:brightness-110">
                        Ship release (+{pending})
                    </button>
                )}
            </div>
        </section>
    );
}

// What you've built: upgrade tiers and synergy bonuses per worker.
function ProgressPanel({ state }) {
    const owned = workers.filter(worker => state.counters[worker.type] > 0);
    if (owned.length === 0) return null;

    return (
        <section className="w-full max-w-sm rounded-lg bg-oxford-blue/20 p-4">
            <h3 className="text-xs uppercase tracking-wider text-white/80 mb-2">Your stack</h3>
            <ul className="flex flex-col gap-2">
                {owned.map(worker => {
                    const tiers = state.upgrades[worker.type];
                    const synergy = getSynergyBonus(state, worker.type);
                    return (
                        <li key={worker.type} className="flex items-center gap-2 text-sm motion-safe:animate-pop-in">
                            <IconTile icon={worker.icon} />
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold leading-tight truncate">{worker.name}</p>
                                <p className="flex gap-1 mt-0.5" aria-label={`${tiers} of ${workerUpgradeTiers.length} upgrades`}>
                                    {workerUpgradeTiers.map((_, i) => (
                                        <span key={i} className={`w-2 h-2 rounded-full ${i < tiers ? 'bg-white' : 'bg-white/25'}`} />
                                    ))}
                                </p>
                            </div>
                            <div className="text-right text-xs leading-tight">
                                <p className="font-semibold">×{formatNumber(getWorkerMultiplier(state, worker.type), 2)} output</p>
                                {synergy > 0 && <p className="text-white/70">+{formatNumber(synergy * 100)}% synergy</p>}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

export default Clicker;
